import { useState, useCallback } from 'react';
import { notification } from 'antd';

/**
 * A custom hook for managing fetch status states across the application.
 *
 * @returns {Object} An object containing status management methods and states
 */

const DEFAULT_ERROR_CONFIG = {
  shouldNotify: true,
  formatTitle: (actionId, error) => `Error ${actionId}`,
  formatMessage: error => error.response?.data?.message || error.message || 'An unknown error occurred',
  duration: 0,
};

export const useFetchStatus = () => {
  // Track loading states for different actions
  const [loadingStates, setLoadingStates] = useState({});

  // Track error states for different actions
  const [errorStates, setErrorStates] = useState({});

  // Track success states for different actions
  const [successStates, setSuccessStates] = useState({});

  // Track confirmations for actions that need user approval
  const [confirmations, setConfirmations] = useState({});

  const isBusy = useCallback(() => {
    return Object.values(loadingStates).some(isLoading => isLoading);
  }, [loadingStates]);

  /**
   * Sets the loading state for a specific action
   * @param {string} actionId - Unique identifier for the action
   * @param {boolean} isLoading - Whether the action is loading
   */
  const setLoading = useCallback((actionId, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [actionId]: isLoading,
    }));

    // Reset error and success when starting a new request
    if (isLoading) {
      setErrorStates(prev => ({
        ...prev,
        [actionId]: null,
      }));
      setSuccessStates(prev => ({
        ...prev,
        [actionId]: false,
      }));
    }
  }, []);

  /**
   * Sets the error state for a specific action
   * @param {string} actionId - Unique identifier for the action
   * @param {*} error - Error object or message
   */
  const setError = useCallback((actionId, error) => {
    setErrorStates(prev => ({
      ...prev,
      [actionId]: error,
    }));

    // Update loading and success states
    setLoadingStates(prev => ({
      ...prev,
      [actionId]: false,
    }));
    setSuccessStates(prev => ({
      ...prev,
      [actionId]: false,
    }));
  }, []);

  /**
   * Sets the success state for a specific action
   * @param {string} actionId - Unique identifier for the action
   * @param {boolean} isSuccess - Whether the action was successful
   */
  const setSuccess = useCallback((actionId, isSuccess) => {
    setSuccessStates(prev => ({
      ...prev,
      [actionId]: isSuccess,
    }));

    // Update loading state
    setLoadingStates(prev => ({
      ...prev,
      [actionId]: false,
    }));
  }, []);

  /**
   * Shows an error notification
   * @param {string} actionId - The action identifier
   * @param {Error} error - The error object
   * @param {Object} customConfig - Custom notification configuration
   */
  const showErrorNotification = useCallback((actionId, error, customConfig = {}) => {
    const config = { ...DEFAULT_ERROR_CONFIG, ...customConfig };

    if (config.shouldNotify) {
      notification.error({
        message: config.formatTitle(actionId, error),
        description: (
          <div>
            <p>{config.formatMessage(error)}</p>
          </div>
        ),
        duration: config.duration,
      });
    }
  }, []);

  /**
   * Enhanced executeFetch with automatic error notification
   * @param {string} actionId - Unique identifier for the action
   * @param {Function} fetchFn - Async function to execute
   * @param {Object} errorConfig - Error notification configuration (optional)
   */
  const execute = useCallback(
    async (actionId, fetchFn, errorConfig = {}) => {
      setLoading(actionId, true);

      try {
        const result = await fetchFn();
        setSuccess(actionId, true);
        return result;
      } catch (error) {
        setError(actionId, error);
        showErrorNotification(actionId, error, errorConfig);
        throw error;
      }
    },
    [setLoading, setSuccess, setError, showErrorNotification]
  );

  /**
   * Request user confirmation before executing an action
   *
   * @param {string} actionId - Unique identifier for the action
   * @param {Object} options - Confirmation options
   * @param {string} options.title - Confirmation dialog title
   * @param {string} options.message - Confirmation message
   * @param {string} options.confirmText - Text for confirm button (default: "Yes")
   * @param {string} options.cancelText - Text for cancel button (default: "No")
   * @param {Function} options.onConfirm - Function to execute when user confirms
   * @param {Function} options.onCancel - Function to execute when user cancels
   */
  const requestConfirmation = useCallback((actionId, options) => {
    const confirmationOptions = {
      title: options.title || 'Confirm Action',
      message: options.message || 'Are you sure you want to proceed?',
      confirmText: options.confirmText || 'Yes',
      cancelText: options.cancelText || 'No',
      onConfirm: options.onConfirm || (() => {}),
      onCancel: options.onCancel || (() => {}),
      isOpen: true,
    };

    setConfirmations(prev => ({
      ...prev,
      [actionId]: confirmationOptions,
    }));
  }, []);

  /**
   * Confirm an action that was previously requested
   * @param {string} actionId - Unique identifier for the action
   */
  const confirmAction = useCallback(
    actionId => {
      const confirmation = confirmations[actionId];
      if (confirmation && confirmation.onConfirm) {
        confirmation.onConfirm();
      }

      // Close the confirmation dialog
      setConfirmations(prev => ({
        ...prev,
        [actionId]: { ...prev[actionId], isOpen: false },
      }));
    },
    [confirmations]
  );

  /**
   * Cancel an action that was previously requested
   * @param {string} actionId - Unique identifier for the action
   */
  const cancelAction = useCallback(
    actionId => {
      const confirmation = confirmations[actionId];
      if (confirmation && confirmation.onCancel) {
        confirmation.onCancel();
      }

      // Close the confirmation dialog
      setConfirmations(prev => ({
        ...prev,
        [actionId]: { ...prev[actionId], isOpen: false },
      }));
    },
    [confirmations]
  );

  /**
   * Check if a confirmation dialog is currently open for an action
   * @param {string} actionId - Unique identifier for the action
   * @returns {boolean} Whether the confirmation dialog is open
   */
  const isConfirmationOpen = useCallback(
    actionId => {
      return !!confirmations[actionId]?.isOpen;
    },
    [confirmations]
  );

  /**
   * Get confirmation options for an action
   * @param {string} actionId - Unique identifier for the action
   * @returns {Object|null} Confirmation options or null if not found
   */
  const getConfirmation = useCallback(
    actionId => {
      return confirmations[actionId] || null;
    },
    [confirmations]
  );

  /**
   * Enhanced executeWithConfirmation with automatic error notification
   */
  const executeWithConfirmation = useCallback(
    (actionId, fetchFn, confirmOptions = {}, errorConfig = {}) => {
      return new Promise((resolve, reject) => {
        requestConfirmation(actionId, {
          ...confirmOptions,
          onConfirm: async () => {
            try {
              const result = await execute(actionId, fetchFn, errorConfig);
              resolve(result);
            } catch (error) {
              reject(error);
            }
          },
          onCancel: () => {
            reject(new Error('User cancelled the action'));
          },
        });
      });
    },
    [requestConfirmation, execute]
  );

  /**
   * Check if a specific action or multiple actions are loading
   * @param {string|string[]} actionId - Unique identifier(s) for the action(s)
   * @returns {boolean} Whether any of the actions are loading
   */
  const isLoading = useCallback(
    actionId => {
      // If actionId is an array, return true if any of the actions are loading
      if (Array.isArray(actionId)) {
        return actionId.some(id => !!loadingStates[id]);
      }
      // Otherwise handle single action
      return !!loadingStates[actionId];
    },
    [loadingStates]
  );

  /**
   * Get the error for a specific action or the first error from multiple actions
   * @param {string|string[]} actionId - Unique identifier(s) for the action(s)
   * @returns {*} The error object or message
   */
  const getError = useCallback(
    actionId => {
      // If actionId is an array, return the first error found
      if (Array.isArray(actionId)) {
        for (const id of actionId) {
          if (errorStates[id]) {
            return errorStates[id];
          }
        }
        return null;
      }
      // Otherwise handle single action
      return errorStates[actionId];
    },
    [errorStates]
  );

  /**
   * Get all errors for multiple actions
   * @param {string[]} actionIds - Array of action identifiers
   * @returns {Object} Object with action ids as keys and errors as values
   */
  const getAllErrors = useCallback(
    actionIds => {
      if (!Array.isArray(actionIds)) {
        return { [actionIds]: errorStates[actionIds] };
      }

      return actionIds.reduce((result, id) => {
        result[id] = errorStates[id];
        return result;
      }, {});
    },
    [errorStates]
  );

  /**
   * Check if a specific action or all of multiple actions were successful
   * @param {string|string[]} actionId - Unique identifier(s) for the action(s)
   * @param {boolean} requireAll - If true, all actions must be successful (default: false)
   * @returns {boolean} Whether the action(s) were successful
   */
  const isSuccess = useCallback(
    (actionId, requireAll = false) => {
      // If actionId is an array
      if (Array.isArray(actionId)) {
        if (requireAll) {
          // All actions must be successful
          return actionId.every(id => !!successStates[id]);
        } else {
          // At least one action must be successful
          return actionId.some(id => !!successStates[id]);
        }
      }
      // Otherwise handle single action
      return !!successStates[actionId];
    },
    [successStates]
  );

  /**
   * Check if any of the actions are in loading state
   * @param {string[]} actionIds - Array of action identifiers
   * @returns {boolean} Whether any of the actions are loading
   */
  const isAnyLoading = useCallback(
    actionIds => {
      if (!Array.isArray(actionIds)) {
        return !!loadingStates[actionIds];
      }
      return actionIds.some(id => !!loadingStates[id]);
    },
    [loadingStates]
  );

  /**
   * Check if all of the actions are in loading state
   * @param {string[]} actionIds - Array of action identifiers
   * @returns {boolean} Whether all of the actions are loading
   */
  const areAllLoading = useCallback(
    actionIds => {
      if (!Array.isArray(actionIds)) {
        return !!loadingStates[actionIds];
      }
      return actionIds.every(id => !!loadingStates[id]);
    },
    [loadingStates]
  );

  /**
   * Reset all states for a specific action
   * @param {string|string[]} actionId - Unique identifier(s) for the action(s)
   */
  const resetStatus = useCallback(actionId => {
    const resetSingleStatus = id => {
      setLoadingStates(prev => ({
        ...prev,
        [id]: false,
      }));
      setErrorStates(prev => ({
        ...prev,
        [id]: null,
      }));
      setSuccessStates(prev => ({
        ...prev,
        [id]: false,
      }));
      setConfirmations(prev => ({
        ...prev,
        [id]: undefined,
      }));
    };

    if (Array.isArray(actionId)) {
      actionId.forEach(resetSingleStatus);
    } else {
      resetSingleStatus(actionId);
    }
  }, []);

  /**
   * Reset all states for all actions
   */
  const resetAllStatuses = useCallback(() => {
    setLoadingStates({});
    setErrorStates({});
    setSuccessStates({});
    setConfirmations({});
  }, []);

  return {
    execute,
    setLoading,
    setError,
    setSuccess,
    isLoading,
    getError,
    getAllErrors,
    isSuccess,
    isAnyLoading,
    areAllLoading,
    resetStatus,
    resetAllStatuses,
    loadingStates,
    errorStates,
    successStates,
    showErrorNotification,
    isBusy,
    // Confirmation methods
    requestConfirmation,
    confirmAction,
    cancelAction,
    isConfirmationOpen,
    getConfirmation,
    executeWithConfirmation,
    confirmations,
  };
};
